import SpecScorecard from './SpecScorecard';
import { scorecardOf, type LeaseSearchDeal, type ScorecardRowDef } from './leaseSearch';

/**
 * The body of a building's card on a tenant's page: our read of it, how it scores against his spec, the facts the
 * contract carries, where it stands, and where those facts come from.
 *
 * ⛔ Every line is the contract's own string. The facts are a list the builder formatted once ("184,587 to 406,650
 * SF"); a fact the roster does not state is simply not in the list - blank is not zero.
 */
export default function LeaseSearchCardBody({
  deal,
  rows,
  unknownTally,
}: {
  deal: LeaseSearchDeal;
  rows: ScorecardRowDef[];
  /** Passed to the scorecard's tally: "checking" on a buy search, "not stated" (the default) on a lease search. */
  unknownTally?: string;
}) {
  const facts = (deal.facts ?? []).filter((f) => f?.label && f?.value);
  const confirming = (Array.isArray(deal.toConfirm) ? deal.toConfirm : []).filter((t) => typeof t === 'string' && t.trim());
  return (
    <>
      {deal.verdict && (
        <div className="portal-lease-read">
          <p className="portal-lease-label">Our read</p>
          <p>{deal.verdict}</p>
        </div>
      )}
      <SpecScorecard rows={rows} scorecard={scorecardOf(deal)} unknownTally={unknownTally} />
      {facts.length > 0 && (
        <dl className="portal-facts portal-lease-facts">
          {facts.map((f) => (
            <div className="portal-fact" key={f.label}>
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {confirming.length > 0 && (
        <div className="portal-lease-confirming">
          <p className="portal-lease-label">We're confirming</p>
          <ul>
            {confirming.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        </div>
      )}
      {deal.status?.label && (
        <div className="portal-lease-stands">
          <p className="portal-lease-label">Where this stands</p>
          <p>
            {deal.status.label}
            {deal.status.asOf && <span className="portal-deal-source"> (as of {deal.status.asOf})</span>}
          </p>
        </div>
      )}
      {deal.factsBasis && <p className="portal-lease-basis">{deal.factsBasis}</p>}
    </>
  );
}
